import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { customerTicketsApi } from '../lib/api';
import { ArrowLeft, MessageSquare, Clock, CheckCircle, XCircle, Send, AlertCircle, User, Headphones, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

interface TicketReply {
  id: string;
  authorType: 'CUSTOMER' | 'STAFF';
  authorId: string;
  message: string;
  createdAt: string;
}

interface Ticket {
  id: string;
  subject: string;
  status: string;
  priority: string;
  department: string;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  product?: { id: string; domain: string; type: string };
  replies: TicketReply[];
}

const statusConfig: Record<string, { icon: any; color: string; bg: string }> = {
  OPEN: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100' },
  AWAITING_REPLY: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  RESOLVED: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
  CLOSED: { icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-100' },
};

const priorityConfig: Record<string, string> = {
  LOW: 'bg-gray-100 text-gray-700',
  MEDIUM: 'bg-blue-100 text-blue-700',
  HIGH: 'bg-orange-100 text-orange-700',
  URGENT: 'bg-red-100 text-red-700',
};

export default function TicketDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    loadTicket();
  }, [id]);

  async function loadTicket() {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await customerTicketsApi.get(id);
      setTicket(response.data as Ticket);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSendReply(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !replyMessage.trim()) return;

    setSending(true);
    setError(null);

    try {
      await customerTicketsApi.reply(id, replyMessage.trim());
      setReplyMessage('');
      loadTicket();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function handleCloseTicket() {
    if (!id) return;
    if (!confirm('Are you sure you want to close this ticket?')) return;

    setClosing(true);
    setError(null);

    try {
      await customerTicketsApi.close(id);
      loadTicket();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setClosing(false);
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="space-y-4">
        <Link to="/tickets" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" />
          {t('tickets.backToTickets')}
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  const config = (statusConfig[ticket.status as keyof typeof statusConfig] ?? statusConfig.OPEN)!;
  const StatusIcon = config.icon;
  const isClosed = ticket.status === 'CLOSED';

  return (
    <div className="space-y-6">
      <Link to="/tickets" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" />
        {t('tickets.backToTickets')}
      </Link>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{ticket.subject}</h1>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <span
                className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium',
                  config.bg,
                  config.color
                )}
              >
                <StatusIcon className="h-4 w-4" />
                {ticket.status.replace('_', ' ')}
              </span>
              <span
                className={clsx(
                  'px-3 py-1 rounded-full text-sm font-medium',
                  priorityConfig[ticket.priority]
                )}
              >
                {ticket.priority}
              </span>
              <span className="text-gray-500 text-sm">{ticket.department}</span>
              {ticket.product && (
                <span className="text-gray-500 text-sm">• {ticket.product.domain}</span>
              )}
            </div>
          </div>
          {!isClosed && (
            <button
              onClick={handleCloseTicket}
              disabled={closing}
              className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
            >
              {closing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              Close Ticket
            </button>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Opened on {formatDate(ticket.createdAt)}
          {ticket.closedAt && ` • Closed on ${formatDate(ticket.closedAt)}`}
        </div>
      </div>

      {/* Replies */}
      <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
        {ticket.replies.map((reply) => (
          <div
            key={reply.id}
            className={clsx(
              'p-6',
              reply.authorType === 'STAFF' && 'bg-blue-50'
            )}
          >
            <div className="flex items-start gap-4">
              <div
                className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  reply.authorType === 'STAFF' ? 'bg-blue-200' : 'bg-gray-200'
                )}
              >
                {reply.authorType === 'STAFF' ? (
                  <Headphones className="h-5 w-5 text-blue-700" />
                ) : (
                  <User className="h-5 w-5 text-gray-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {reply.authorType === 'STAFF' ? 'Support Team' : 'You'}
                  </span>
                  <span className="text-sm text-gray-500">{formatDate(reply.createdAt)}</span>
                </div>
                <div className="mt-2 text-gray-700 whitespace-pre-wrap">{reply.message}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Reply Form */}
      {!isClosed ? (
        <form onSubmit={handleSendReply} className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('tickets.addReply')}
          </label>
          <textarea
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            required
            rows={4}
            placeholder={t('tickets.replyHint')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={sending || !replyMessage.trim()}
              className={clsx(
                'inline-flex items-center gap-2 px-6 py-2 rounded-lg font-medium text-white transition-colors',
                sending || !replyMessage.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-brand-600 hover:bg-brand-700'
              )}
            >
              {sending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
              Send Reply
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-600">
          <XCircle className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p>{t('tickets.closedNotice')}</p>
        </div>
      )}
    </div>
  );
}
